import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home, Radio, Activity, ShieldAlert, TrendingUp, Calendar,
  Megaphone, BookOpen, ExternalLink, Info, ChevronLeft, ChevronRight,
  ChevronDown, Star, LogIn, Zap, LogOut,
} from 'lucide-react';
import { useWatchlistContext } from '@/contexts/WatchlistContext';
import { useAuth } from '@/contexts/AuthContext';
import logo from '../assets/logo.png';
import { useAnnouncement } from '@/contexts/AnnouncementContext';
import AuthModal from './AuthModal';
import PricingModal from './PricingModal';

// ── NavItem ────────────────────────────────────────────────────────────────────

function NavItem({
  to, icon: Icon, label, collapsed, active, sub = false, badge, onClick,
}: {
  to: string;
  icon: React.ElementType;
  label: string;
  collapsed: boolean;
  active: boolean;
  sub?: boolean;
  badge?: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      title={collapsed ? label : undefined}
      className={[
        'relative flex items-center gap-2.5 rounded-md text-sm transition-all duration-150',
        sub ? 'px-2 py-1.5' : 'px-2.5 py-2',
        active
          ? 'bg-blue-50 text-blue-600 font-semibold'
          : 'text-gray-600 hover:text-blue-500 hover:bg-blue-50',
      ].join(' ')}
    >
      <Icon className={`flex-shrink-0 ${sub ? 'h-3.5 w-3.5' : 'h-4 w-4'}`} />
      {!collapsed && (
        <>
          <span className="flex-1 truncate leading-none">{label}</span>
          {badge}
        </>
      )}
      {collapsed && badge && (
        <span className="absolute -top-0.5 -right-0.5 scale-75">{badge}</span>
      )}
    </Link>
  );
}

// ── Sidebar ────────────────────────────────────────────────────────────────────

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onNavClick?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle, onNavClick }) => {
  const location = useLocation();
  const { newCount } = useAnnouncement();
  const { items: watchlistItems } = useWatchlistContext();
  const { user, plan, signOut } = useAuth();
  const isRadarActive = location.pathname.startsWith('/radar');
  const [radarOpen, setRadarOpen] = useState(isRadarActive);
  const [showAuth, setShowAuth] = useState(false);
  const [showPricing, setShowPricing] = useState(false);

  const active = (path: string) => location.pathname === path;

  const newBadge = newCount > 0 ? (
    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] bg-blue-500 text-white text-[10px] font-bold px-1 rounded-full leading-none">
      {newCount}
    </span>
  ) : null;

  return (
    <aside
      className={`flex flex-col h-full bg-white border-r border-gray-200 transition-all duration-200 ${collapsed ? 'w-16' : 'w-60'}`}
    >
      {/* Logo + collapse toggle */}
      <div className="flex items-center justify-between h-16 px-3 border-b border-gray-200 flex-shrink-0">
        <Link to="/" onClick={onNavClick} className="flex items-center gap-2.5 min-w-0 overflow-hidden">
          <img src={logo} alt="DomBase" className="w-8 h-8 rounded-md flex-shrink-0" />
          {!collapsed && (
            <div className="min-w-0">
              <div className="text-sm font-bold text-gray-900 leading-none">DomBase</div>
            </div>
          )}
        </Link>
        <button
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="ml-1 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      {/* Main nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        <NavItem to="/" icon={Home} label="Home" collapsed={collapsed} active={active('/')} onClick={onNavClick} />

        {/* Radar expandable group */}
        <div>
          <button
            onClick={() => { if (!collapsed) setRadarOpen(o => !o); }}
            title={collapsed ? 'Radar' : undefined}
            className={[
              'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-all duration-150',
              isRadarActive ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:text-blue-500 hover:bg-blue-50',
            ].join(' ')}
          >
            <Radio className="h-4 w-4 flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left font-medium leading-none">Radar</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-150 ${radarOpen ? '' : '-rotate-90'}`} />
              </>
            )}
          </button>

          {!collapsed && radarOpen && (
            <div className="mt-0.5 ml-2.5 pl-3 border-l-2 border-gray-200 space-y-0.5">
              <NavItem to="/radar/status" icon={Activity} label="Status" collapsed={false} active={active('/radar/status')} sub onClick={onNavClick} />
              <NavItem to="/radar/news" icon={ShieldAlert} label="Security News" collapsed={false} active={active('/radar/news')} sub onClick={onNavClick} />
            </div>
          )}
        </div>

        <NavItem to="/keywords" icon={TrendingUp} label="Top Keywords" collapsed={collapsed} active={active('/keywords')} onClick={onNavClick} />
        <NavItem
          to="/watchlist"
          icon={Star}
          label="Watchlist"
          collapsed={collapsed}
          active={active('/watchlist')}
          onClick={onNavClick}
          badge={watchlistItems.length > 0 ? (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] bg-yellow-400 text-white text-[10px] font-bold px-1 rounded-full leading-none">
              {watchlistItems.length}
            </span>
          ) : undefined}
        />
        <NavItem to="/expiring" icon={Calendar} label="Expiring Domains" collapsed={collapsed} active={active('/expiring')} onClick={onNavClick} />
        <NavItem to="/announcement" icon={Megaphone} label="Updates" collapsed={collapsed} active={active('/announcement')} badge={newBadge} onClick={onNavClick} />
      </nav>

      {/* Footer: secondary links + auth */}
      <div className="flex-shrink-0 border-t border-gray-200 py-3 px-2 space-y-0.5">
        <NavItem to="/guide" icon={BookOpen} label="Guide" collapsed={collapsed} active={active('/guide')} onClick={onNavClick} />
        <NavItem to="/resources" icon={ExternalLink} label="Resources" collapsed={collapsed} active={active('/resources')} onClick={onNavClick} />
        <NavItem to="/about" icon={Info} label="About" collapsed={collapsed} active={active('/about')} onClick={onNavClick} />

        {/* Auth / Plan section */}
        <div className="pt-2 border-t border-gray-100 mt-2">
          {user ? (
            <>
              {plan === 'free' && (
                <button
                  onClick={() => setShowPricing(true)}
                  title={collapsed ? 'Go Premium' : undefined}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                >
                  <Zap className="h-4 w-4 flex-shrink-0" />
                  {!collapsed && <span className="truncate leading-none">Go Premium</span>}
                </button>
              )}
              {plan === 'premium' && !collapsed && (
                <div className="px-2.5 py-1.5">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                    <Star className="h-3 w-3 fill-blue-600" /> Premium
                  </span>
                </div>
              )}
              <button
                onClick={signOut}
                title={collapsed ? 'Sign out' : undefined}
                className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <LogOut className="h-4 w-4 flex-shrink-0" />
                {!collapsed && (
                  <div className="min-w-0 text-left">
                    <p className="text-xs font-medium text-gray-700 truncate leading-none">{user.email}</p>
                    <p className="text-xs text-gray-400 leading-none mt-0.5">Sign out</p>
                  </div>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={() => setShowAuth(true)}
              title={collapsed ? 'Sign in' : undefined}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
            >
              <LogIn className="h-4 w-4 flex-shrink-0" />
              {!collapsed && <span className="leading-none">Sign in</span>}
            </button>
          )}
        </div>
      </div>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      {showPricing && (
        <PricingModal
          onClose={() => setShowPricing(false)}
          onSignIn={() => { setShowPricing(false); setShowAuth(true); }}
        />
      )}
    </aside>
  );
};

export default Sidebar;
